/**
 * Billing panel embedded in the user's Profile page.
 *
 * Replaces the previous hardcoded "Professional Plan / Renews on January 15,
 * 2025" placeholder with live data from `/api/v1/billing/*`.
 *
 * Three states:
 *   - Stripe not configured (503 from any endpoint) → muted disabled card.
 *   - No active subscription → list of available plans + Subscribe buttons.
 *   - Active subscription → current plan summary + Manage button + invoices.
 */

import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { formatCurrency, formatDate } from "../../i18n/format";
import {
  useBillingCheckoutCreate,
  useBillingInvoicesRetrieve,
  useBillingPlansRetrieve,
  useBillingPortalCreate,
  useBillingSubscriptionRetrieve,
} from "../../services/billing/generated/billing/billing";
import type { Invoice, Subscription } from "../../services/billing/generated/model";
import "./BillingSection.css";

interface Plan {
  priceId: string;
  name: string;
  amount?: number;
  currency?: string;
  interval?: string;
  description?: string;
}

interface SubscriptionResponse {
  subscription: Subscription | null;
}

const formatAmount = (amount: number | undefined, currency = "usd"): string => {
  if (amount == null) return "";
  return formatCurrency(amount, currency.toUpperCase(), { minimumFractionDigits: 2 });
};

const formatShortDate = (iso: string | null | undefined): string =>
  formatDate(iso, { year: "numeric", month: "short", day: "numeric" });

const SubscriptionStatusBadge = ({ status }: { status: string }) => {
  const tone =
    status === "active" || status === "trialing"
      ? "billing-section__badge--ok"
      : status === "past_due" || status === "unpaid"
        ? "billing-section__badge--warn"
        : "billing-section__badge--neutral";
  return <span className={`billing-section__badge ${tone}`}>{status.replace(/_/g, " ")}</span>;
};

const BillingSection = () => {
  const { t } = useTranslation(["company", "common"]);
  const [showInvoices, setShowInvoices] = useState(false);

  const subQuery = useBillingSubscriptionRetrieve();
  const plansQuery = useBillingPlansRetrieve();
  const invoicesQuery = useBillingInvoicesRetrieve({
    query: { enabled: showInvoices },
  });

  const checkoutMutation = useBillingCheckoutCreate();
  const portalMutation = useBillingPortalCreate();

  // Treat a 503 from any of these as "billing not configured" — the panel
  // stays mounted but shows a muted message instead of an error.
  const stripeUnavailable = [subQuery, plansQuery].some(
    (q) =>
      q.error != null &&
      // biome-ignore lint/suspicious/noExplicitAny: axios error shape
      ((q.error as any)?.response?.status === 503 ||
        // biome-ignore lint/suspicious/noExplicitAny: axios error shape
        (q.error as any)?.status === 503),
  );

  const subscription = (subQuery.data as SubscriptionResponse | undefined)?.subscription ?? null;
  const plans = (plansQuery.data as { plans?: Plan[] } | undefined)?.plans ?? [];

  const onSubscribe = (priceId: string) => {
    checkoutMutation.mutate(
      { data: { priceId } },
      {
        onSuccess: (resp) => {
          // biome-ignore lint/suspicious/noExplicitAny: orval narrows differently across modules
          const url = (resp as any)?.url;
          if (url) window.location.href = url;
        },
      },
    );
  };

  const onManage = () => {
    portalMutation.mutate(undefined, {
      onSuccess: (resp) => {
        // biome-ignore lint/suspicious/noExplicitAny: see above
        const url = (resp as any)?.url;
        if (url) window.location.href = url;
      },
    });
  };

  if (stripeUnavailable) {
    return (
      <section className="profile-page__card profile-page__card--subscription billing-section--muted">
        <h2 className="profile-page__card-title profile-page__card-title--light">
          <i className="fa-regular fa-file-lines profile-page__card-icon profile-page__card-icon--white" />
          {t("billing.title")}
        </h2>
        <p className="billing-section__muted-msg">{t("billing.notEnabled")}</p>
      </section>
    );
  }

  return (
    <section className="profile-page__card profile-page__card--subscription">
      <h2 className="profile-page__card-title profile-page__card-title--light">
        <i className="fa-regular fa-file-lines profile-page__card-icon profile-page__card-icon--white" />
        {t("billing.title")}
      </h2>

      {subQuery.isLoading ? (
        <p className="billing-section__loading">
          <i className="fa-solid fa-spinner fa-spin" /> {t("common:states.loading")}
        </p>
      ) : subscription && subscription.isActive ? (
        <ActiveSubscription
          subscription={subscription}
          onManage={onManage}
          managePending={portalMutation.isPending}
          onToggleInvoices={() => setShowInvoices((v) => !v)}
          showInvoices={showInvoices}
        />
      ) : subscription && !subscription.isActive ? (
        <CanceledSubscription
          subscription={subscription}
          plans={plans}
          onSubscribe={onSubscribe}
          checkoutPending={checkoutMutation.isPending}
        />
      ) : (
        <NoSubscription
          plans={plans}
          plansLoading={plansQuery.isLoading}
          onSubscribe={onSubscribe}
          checkoutPending={checkoutMutation.isPending}
        />
      )}

      {showInvoices && (
        <InvoicesList
          invoices={(invoicesQuery.data as { results?: Invoice[] } | undefined)?.results ?? []}
          loading={invoicesQuery.isLoading}
        />
      )}
    </section>
  );
};

interface ActiveProps {
  subscription: Subscription;
  onManage: () => void;
  managePending: boolean;
  onToggleInvoices: () => void;
  showInvoices: boolean;
}

const ActiveSubscription = ({ subscription, onManage, managePending, onToggleInvoices, showInvoices }: ActiveProps) => {
  const { t } = useTranslation("company");
  return (
    <div className="profile-page__subscription-content">
      <div className="profile-page__subscription-info">
        <p className="profile-page__plan">
          <i className="fa-solid fa-circle-check profile-page__plan-check" aria-hidden />
          {subscription.planName || t("billing.activeSubscription")}
          <SubscriptionStatusBadge status={subscription.status} />
        </p>
        <p className="profile-page__renewal">
          {subscription.cancelAtPeriodEnd
            ? t("billing.endsOn", { date: formatShortDate(subscription.currentPeriodEnd) })
            : t("billing.renewsOn", { date: formatShortDate(subscription.currentPeriodEnd) })}
        </p>
        <button
          type="button"
          className="profile-page__billing-link billing-section__link-btn"
          onClick={onToggleInvoices}
        >
          {showInvoices ? t("billing.hideInvoices") : t("billing.viewHistory")}
        </button>
      </div>
      <button type="button" className="profile-page__manage-btn" onClick={onManage} disabled={managePending}>
        {managePending ? t("billing.opening") : t("billing.managePlan")}
      </button>
    </div>
  );
};

interface NoSubProps {
  plans: Plan[];
  plansLoading: boolean;
  onSubscribe: (priceId: string) => void;
  checkoutPending: boolean;
}

const NoSubscription = ({ plans, plansLoading, onSubscribe, checkoutPending }: NoSubProps) => {
  const { t } = useTranslation("company");
  return (
    <div className="billing-section__no-sub">
      <p className="billing-section__no-sub-msg">{t("billing.noPlan")}</p>
      {plansLoading ? (
        <p className="billing-section__loading">
          <i className="fa-solid fa-spinner fa-spin" /> {t("billing.loadingPlans")}
        </p>
      ) : plans.length === 0 ? (
        <p className="billing-section__muted-msg">{t("billing.noPlans")}</p>
      ) : (
        <div className="billing-section__plans">
          {plans.map((p) => (
            <PlanCard key={p.priceId} plan={p} onSubscribe={() => onSubscribe(p.priceId)} disabled={checkoutPending} />
          ))}
        </div>
      )}
    </div>
  );
};

interface CanceledProps {
  subscription: Subscription;
  plans: Plan[];
  onSubscribe: (priceId: string) => void;
  checkoutPending: boolean;
}

const CanceledSubscription = ({ subscription, plans, onSubscribe, checkoutPending }: CanceledProps) => {
  const { t } = useTranslation("company");
  return (
    <div className="billing-section__no-sub">
      <p className="billing-section__no-sub-msg">
        <Trans
          t={t}
          i18nKey="billing.previousPlan"
          values={{ plan: subscription.planName || t("billing.subscriptionFallback") }}
          components={{ badge: <SubscriptionStatusBadge status={subscription.status} /> }}
        />
      </p>
      {plans.length > 0 && (
        <div className="billing-section__plans">
          {plans.map((p) => (
            <PlanCard key={p.priceId} plan={p} onSubscribe={() => onSubscribe(p.priceId)} disabled={checkoutPending} />
          ))}
        </div>
      )}
    </div>
  );
};

const PlanCard = ({ plan, onSubscribe, disabled }: { plan: Plan; onSubscribe: () => void; disabled: boolean }) => {
  const { t } = useTranslation("company");
  return (
    <div className="billing-section__plan-card">
      <h3>{plan.name}</h3>
      {plan.amount != null && (
        <p className="billing-section__plan-amount">
          {formatAmount(plan.amount, plan.currency)}
          {plan.interval && <span className="billing-section__plan-interval"> / {plan.interval}</span>}
        </p>
      )}
      {plan.description && <p className="billing-section__plan-desc">{plan.description}</p>}
      <button type="button" className="billing-section__subscribe-btn" onClick={onSubscribe} disabled={disabled}>
        {disabled ? t("billing.redirecting") : t("billing.subscribe")}
      </button>
    </div>
  );
};

const InvoicesList = ({ invoices, loading }: { invoices: Invoice[]; loading: boolean }) => {
  const { t } = useTranslation(["company", "common"]);
  if (loading) {
    return (
      <p className="billing-section__loading billing-section__invoices-loading">
        <i className="fa-solid fa-spinner fa-spin" /> {t("billing.loadingInvoices")}
      </p>
    );
  }
  if (!invoices.length) {
    return <p className="billing-section__muted-msg billing-section__invoices-empty">{t("billing.noInvoices")}</p>;
  }
  return (
    <div className="billing-section__invoices">
      <table>
        <thead>
          <tr>
            <th>{t("billing.table.invoice")}</th>
            <th>{t("billing.table.period")}</th>
            <th>{t("billing.table.amount")}</th>
            <th>{t("common:labels.status")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id}>
              <td>{inv.number || inv.stripeInvoiceId}</td>
              <td>
                {formatShortDate(inv.periodStart)} – {formatShortDate(inv.periodEnd)}
              </td>
              <td>{formatAmount(inv.amountPaid, inv.currency)}</td>
              <td>
                <SubscriptionStatusBadge status={inv.status} />
              </td>
              <td>
                {inv.hostedInvoiceUrl && (
                  <a
                    href={inv.hostedInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="billing-section__invoice-link"
                  >
                    {t("common:actions.view")}
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BillingSection;
